// PlaybookPanel.jsx — "Open playbook draft" side panel
// GET /playbook/{uid}/matters/{matter_id}?mode=…&document_type=… → facts check UI
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import {
  getMatter,
  resolveContradiction,
  factAction,
  addFact,
  intake,
  generateDraft,
  getTypes,
  getDraft,
  exportDraft,
} from "./playbookApi";
import PlaybookDraftView from "./playbookdraft";
import "./playbook.css";

// ── small helpers ────────────────────────────────────────────────────────────
const MODE_LABELS = {
  quick: "Quick draft",
  professional: "Professional draft",
  plain: "Plain-language draft",
  simple: "Simple draft",
};
const modeLabel = (m) =>
  MODE_LABELS[m] || `${String(m).charAt(0).toUpperCase()}${String(m).slice(1)} draft`;

const sourceText = (f) =>
  f?.source_type === "uploaded_document"
    ? `${f.source_document || "document"}${f.page ? `, p. ${f.page}` : ""}`
    : "you";

const factBadge = (f) => {
  const cls = String(f?.classification || "").toUpperCase();
  if (cls === "DISPUTED") return { text: "Disputed", tone: "danger" };
  if (cls === "ALLEGED") return { text: "Alleged", tone: "warn" };
  if (cls === "UNKNOWN" || cls === "UNVERIFIED") return { text: "Not verified", tone: "muted" };
  if (f?.confirmed_by_user) return { text: "Confirmed", tone: "ok" };
  if (f?.source_type === "uploaded_document") return { text: "From document", tone: "info" };
  return { text: "Confirmed", tone: "ok" };
};

const deadlineRight = (d) => {
  if (!d) return { text: "—", tone: "muted" };
  if (d.status === "met") return { text: `${d.date_text} ✓`, tone: "ok" };
  if (!d.date_text) return { text: "—", tone: "muted" };
  if (d.status === "overdue" || d.status === "missed" || (typeof d.days_left === "number" && d.days_left < 0))
    return { text: `${d.date_text} (overdue)`, tone: "danger" };
  if (typeof d.days_left === "number")
    return {
      text: `${d.date_text} (${d.days_left} day${d.days_left === 1 ? "" : "s"} left)`,
      tone: d.days_left <= 5 ? "warn" : "strong",
    };
  return { text: d.date_text, tone: "strong" };
};

const isConflictReason = (m) => /conflict/i.test(m?.reason || "");

const isDraft = (d) => d && typeof d === "object" && Array.isArray(d.sections);
const draftIdOf = (d) => d?.draft_id || d?.id || d?.draft?.draft_id || null;
const unwrapDraft = (d) => (isDraft(d) ? d : isDraft(d?.draft) ? d.draft : null);

// ─────────────────────────────────────────────────────────────────────────────
const PlaybookPanel = ({
  userId,
  matterId,
  mode: initialMode = "professional",
  documentType: initialDocType = null,
  sessionId = null,
  refreshKey,
  onClose,
  width,
  isResizing,
  onResizeStart,
  notify = () => {},
}) => {
  const [matter, setMatter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null); // which action is running
  const [mode, setMode] = useState(initialMode || "professional");
  const [docType, setDocType] = useState(initialDocType);
  const [modes, setModes] = useState(["quick", "professional"]);
  const [conflictInput, setConflictInput] = useState({}); // cid → text
  const [editing, setEditing] = useState(null); // { factId, value }
  const [missingInput, setMissingInput] = useState({}); // key → text
  const [freeText, setFreeText] = useState("");
  const [draft, setDraft] = useState(null);
  const [view, setView] = useState("facts"); // "facts" | "draft"
  const abortRef = useRef(null);

  // ── load matter ───────────────────────────────────────────────────────────
  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!userId || !matterId) return;
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      if (!silent) setLoading(true);
      setError("");
      try {
        const data = await getMatter(userId, matterId, { mode, documentType: docType }, ctrl.signal);
        setMatter(data);
        if (!docType && Array.isArray(data?.document_types) && data.document_types[0]) {
          setDocType(data.document_types[0]);
        }
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || "Could not load the matter.");
      } finally {
        if (abortRef.current === ctrl) setLoading(false);
      }
    },
    [userId, matterId, mode, docType]
  );

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load, refreshKey]);

  // available modes from /playbook/types (optional — fails silently)
  useEffect(() => {
    if (!docType) return;
    let alive = true;
    getTypes()
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res) ? res : res?.types || res?.items || [];
        const t = list.find((x) => [x?.document_type, x?.key, x?.id, x?.type].includes(docType));
        const raw = t?.modes;
        if (Array.isArray(raw) && raw.length) {
          setModes(raw.map((m) => (typeof m === "string" ? m : m?.key || m?.id || m?.mode)).filter(Boolean));
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [docType]);

  // matter-la already draft irundha latest-ai background-la load pannu ("View latest draft")
  useEffect(() => {
    const drafts = matter?.drafts;
    if (!Array.isArray(drafts) || !drafts.length || draft) return;
    const last = drafts[drafts.length - 1];
    if (unwrapDraft(last)) return setDraft(unwrapDraft(last));
    const id = typeof last === "string" ? last : draftIdOf(last);
    if (id) getDraft(userId, id).then((d) => unwrapDraft(d) && setDraft({ draft_id: id, ...unwrapDraft(d) })).catch(() => {});
  }, [matter, draft, userId]);

  // ── derived ───────────────────────────────────────────────────────────────
  const checkpoint = matter?.checkpoint || {};
  const facts = useMemo(() => (matter?.facts || []).filter((f) => f.active !== false), [matter]);
  const keyedFacts = facts.filter((f) => f.key);
  const otherFacts = facts.filter((f) => !f.key);
  const openConflicts = (matter?.contradictions || []).filter((c) => c.status !== "resolved");
  const conflictFactIds = new Set(openConflicts.flatMap((c) => c.fact_ids || []));
  const missing = (checkpoint.missing || matter?.missing || []).filter((m) => !isConflictReason(m));
  const blockingCount = (checkpoint.blocking || []).filter((m) => !isConflictReason(m)).length;
  const conflictCount = openConflicts.length;
  const canGenerate = !!matter && blockingCount === 0 && conflictCount === 0;
  const deadlines = checkpoint.deadlines?.items || [];
  const stage = view === "draft" && draft ? 3 : 2;

  // ── actions ───────────────────────────────────────────────────────────────
  const q = { mode, documentType: docType };
  const v = () => matter?.version;

  const run = async (tag, fn, okMsg) => {
    setBusy(tag);
    try {
      const res = await fn();
      if (okMsg) notify("success", okMsg);
      // Response-la updated matter vandha adhaiye use pannu; illana reload
      const m = res?.matter || res;
      if (m && Array.isArray(m.facts) && m.checkpoint) setMatter(m);
      else await load({ silent: true });
      return res ?? true;
    } catch (e) {
      if (e.status === 409) {
        notify("warning", "This matter changed elsewhere — reloaded the latest facts. Please try again.");
        await load({ silent: true });
      } else {
        notify("error", e.message || "Something went wrong.");
      }
      return null;
    } finally {
      setBusy(null);
    }
  };

  const chooseConflict = (cid, factId) =>
    run(`cf-${cid}`, () => resolveContradiction(userId, matterId, cid, { factId, version: v() }, q), "Conflict resolved.");

  const typeConflict = (cid) => {
    const value = (conflictInput[cid] || "").trim();
    if (!value) return;
    run(`cf-${cid}`, () => resolveContradiction(userId, matterId, cid, { value, version: v() }, q), "Conflict resolved.").then(
      (r) => r !== null && setConflictInput((p) => ({ ...p, [cid]: "" }))
    );
  };

  // PATCH /facts/{fid} { action: "confirm" | "retract", expected_version }
  const confirmFact = (f) =>
    run(`f-${f.fact_id}`, () => factAction(userId, matterId, f.fact_id, { action: "confirm", version: v() }, q));

  const rejectFact = (f) =>
    run(`f-${f.fact_id}`, () => factAction(userId, matterId, f.fact_id, { action: "retract", version: v() }, q));

  // "Change" → POST /facts { key, value, expected_version }
  const saveEdit = () => {
    if (!editing) return;
    const value = editing.value.trim();
    if (!value) return;
    const f = facts.find((x) => x.fact_id === editing.factId);
    if (!f) return setEditing(null);
    if (value === String(f.value ?? "").trim()) return setEditing(null);
    run(`f-${f.fact_id}`, () =>
      addFact(userId, matterId, { key: f.key, label: f.label, value, version: v() }, q), "Fact updated."
    ).then((r) => r !== null && setEditing(null));
  };

  const saveMissing = (m) => {
    const value = (missingInput[m.key] || "").trim();
    if (!value) return;
    run(`m-${m.key}`, () => addFact(userId, matterId, { key: m.key, label: m.label, value, version: v() }, q)).then(
      (r) => r !== null && setMissingInput((p) => ({ ...p, [m.key]: "" }))
    );
  };

  const submitFreeText = () => {
    const details = freeText.trim();
    if (!details) return;
    run(
      "intake",
      () => intake(userId, { matterId, details, documentType: docType, mode, version: v() }),
      "Facts updated."
    ).then((r) => r !== null && setFreeText(""));
  };

  // POST /generate → full draft (sections, validation, dashboard…) ~20–40 s
  const handleGenerate = async () => {
    setBusy("generate");
    try {
      const res = await generateDraft(userId, { matterId, documentType: docType, mode, sessionId });
      const d = unwrapDraft(res);
      if (!d) throw new Error("Draft response was empty.");
      setDraft({ ...d, draft_id: draftIdOf(res) || draftIdOf(d) });
      setView("draft");
      notify("success", "Draft ready.");
      load({ silent: true }); // matter version maarum
    } catch (e) {
      notify("error", e.message || "Could not generate the draft.");
    } finally {
      setBusy(null);
    }
  };

  // GET /drafts/{draft_id}/export?format=docx|pdf
  const handleExport = async (format) => {
    const id = draftIdOf(draft);
    if (!id) return notify("error", "This draft has no id yet — reopen the panel and try again.");
    setBusy(`export-${format}`);
    try {
      await exportDraft(userId, id, format, (draft?.title || matter?.title || "draft").replace(/[^\w.-]+/g, "_").slice(0, 80));
    } catch (e) {
      notify("error", e.message);
    } finally {
      setBusy(null);
    }
  };

  // ── render pieces ─────────────────────────────────────────────────────────
  const Badge = ({ f }) => {
    const b = factBadge(f);
    return <span className={`pbp-badge pbp-${b.tone}`}>{b.text}</span>;
  };

  const renderFactRow = (f) => {
    const isEditing = editing?.factId === f.fact_id;
    const rowBusy = busy === `f-${f.fact_id}`;
    const needsConfirm = !f.confirmed_by_user && f.source_type === "uploaded_document";
    return (
      <tr key={f.fact_id} className={conflictFactIds.has(f.fact_id) ? "pbp-row-conflict" : ""}>
        <td className="pbp-label">{f.label}</td>
        <td>
          {isEditing ? (
            <div className="pbp-inline">
              <input
                autoFocus
                className="pbp-input"
                value={editing.value}
                onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveEdit();
                  if (e.key === "Escape") setEditing(null);
                }}
              />
            </div>
          ) : (
            <>
              <Badge f={f} /> {f.value}
              {conflictFactIds.has(f.fact_id) && <span className="pbp-badge pbp-danger pbp-ml">Conflict</span>}
            </>
          )}
        </td>
        <td className="pbp-src">
          {sourceText(f)}
          {f.quote && f.source_type === "uploaded_document" && <em className="pbp-quote">“{f.quote}”</em>}
        </td>
        <td className="pbp-actions">
          {isEditing ? (
            <>
              <button className="pbp-btn" disabled={rowBusy} onClick={saveEdit}>Save</button>
              <button className="pbp-btn" onClick={() => setEditing(null)}>Cancel</button>
            </>
          ) : (
            <>
              {needsConfirm && (
                <button className="pbp-btn" disabled={rowBusy} onClick={() => confirmFact(f)}>Confirm</button>
              )}
              <button
                className="pbp-btn"
                disabled={rowBusy}
                onClick={() => setEditing({ factId: f.fact_id, value: f.value || "" })}
              >
                Change
              </button>
              <button className="pbp-btn pbp-icon" disabled={rowBusy} title="Remove" onClick={() => rejectFact(f)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </>
          )}
        </td>
      </tr>
    );
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className={`doc-side-panel pbp-panel${isResizing ? " resizing" : ""}`} style={{ width: `${width}px` }}>
      <div
        className="doc-resize-handle"
        onPointerDown={onResizeStart}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize playbook panel"
      >
        <div className="doc-resize-grip"><span></span><span></span><span></span><span></span></div>
      </div>

      <div className="doc-side-panel-header">
        {(() => {
          const heading = view === "draft" && draft?.title ? draft.title : matter?.title || "Playbook draft";
          return (
            <h6 className="doc-side-panel-title pbp-title" title={heading}>
              {heading}
            </h6>
          );
        })()}
        <div className="doc-side-panel-actions">
          {view === "draft" && draft && (
            <>
              <button type="button" className="pbp-btn" disabled={!!busy} onClick={() => handleExport("docx")}>
                {busy === "export-docx" ? "…" : "Word"}
              </button>
              <button type="button" className="pbp-btn" disabled={!!busy} onClick={() => handleExport("pdf")}>
                {busy === "export-pdf" ? "…" : "PDF"}
              </button>
            </>
          )}
          <button type="button" className="doc-panel-btn" onClick={() => load()} disabled={loading} title="Refresh">
            <FontAwesomeIcon icon={faRotateRight} spin={loading} />
          </button>
          <button type="button" className="doc-panel-close" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      <div className="doc-side-panel-body pbp-body">
        {loading && !matter && <div className="pbp-empty">Loading matter…</div>}
        {error && (
          <div className="pbp-error">
            {error} <button className="pbp-btn" onClick={() => load()}>Retry</button>
          </div>
        )}

        {matter && (
          <>
            {/* steps */}
            <div className="pbp-steps">
              {["Start", "Facts check", "Draft ready"].map((s, i) => (
                <span key={s} className={`pbp-step${stage === i + 1 ? " active" : stage > i + 1 ? " done" : ""}`}>
                  {i + 1}. {s}
                </span>
              ))}
            </div>
            {busy === "generate" && (
              <div className="pbp-card pbp-drafting">⏳ Drafting… (about 20–40 seconds)</div>
            )}
            <div className="pbp-meta">
              {matter.title} · <code>{matter.matter_id || matterId}</code>
              {matter.version ? ` · v${matter.version}` : ""}
            </div>

            {view === "draft" && draft ? (
              <PlaybookDraftView
                userId={userId}
                draft={draft}
                draftId={draftIdOf(draft)}
                onDraftChange={(d) => setDraft((prev) => ({ ...d, draft_id: draftIdOf(d) || draftIdOf(prev) }))}
                onBackToFacts={() => setView("facts")}
                notify={notify}
              />
            ) : (
            <>

            {/* deadlines */}
            {deadlines.length > 0 && (
              <section>
                <h6 className="pbp-h">Deadlines (computed by rule, not by AI)</h6>
                <div className="pbp-card">
                  {deadlines.map((d) => {
                    const r = deadlineRight(d);
                    return (
                      <div key={d.key} className="pbp-deadline" title={[d.source, d.note].filter(Boolean).join(" — ")}>
                        <div>
                          <span className={d.status === "met" ? "pbp-ok-text" : ""}>{d.label}</span>
                          <span className="pbp-rule"> · {d.rule}</span>
                        </div>
                        <div className={`pbp-dl-date pbp-${r.tone}-text`}>{r.text}</div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* conflicts */}
            {openConflicts.length > 0 && (
              <section>
                <h6 className="pbp-h">Conflicts — choose the correct value</h6>
                {openConflicts.map((c) => {
                  const cBusy = busy === `cf-${c.contradiction_id}`;
                  return (
                    <div key={c.contradiction_id} className="pbp-conflict">
                      <div className="pbp-conflict-title">{c.label}</div>
                      {(c.facts || []).map((f) => (
                        <div key={f.fact_id} className="pbp-choice">
                          <button
                            className="pbp-btn"
                            disabled={cBusy}
                            onClick={() => chooseConflict(c.contradiction_id, f.fact_id)}
                          >
                            Use {f.value}
                          </button>
                          <span className="pbp-src"> ({sourceText(f)})</span>
                          {f.quote && f.source_type === "uploaded_document" && (
                            <div className="pbp-quote">“{f.quote}”</div>
                          )}
                        </div>
                      ))}
                      <div className="pbp-inline">
                        <input
                          className="pbp-input"
                          placeholder="or type the correct value"
                          value={conflictInput[c.contradiction_id] || ""}
                          onChange={(e) => setConflictInput((p) => ({ ...p, [c.contradiction_id]: e.target.value }))}
                          onKeyDown={(e) => e.key === "Enter" && typeConflict(c.contradiction_id)}
                        />
                        <button className="pbp-btn" disabled={cBusy} onClick={() => typeConflict(c.contradiction_id)}>
                          Save
                        </button>
                      </div>
                    </div>
                  );
                })}
              </section>
            )}

            {/* facts */}
            <section>
              <h6 className="pbp-h">Facts</h6>
              <table className="pbp-table">
                <thead>
                  <tr><th>Detail</th><th>Value</th><th>Source</th><th></th></tr>
                </thead>
                <tbody>{keyedFacts.map(renderFactRow)}</tbody>
              </table>
            </section>

            {otherFacts.length > 0 && (
              <section>
                <h6 className="pbp-h">Other facts</h6>
                <ul className="pbp-other">
                  {otherFacts.map((f) => (
                    <li key={f.fact_id}>
                      <Badge f={f} /> {f.label}: {f.value}{" "}
                      <span className="pbp-src">
                        ({sourceText(f)}){f.quote && <em className="pbp-quote"> “{f.quote}”</em>}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* still needed */}
            <section>
              <h6 className="pbp-h">Still needed</h6>
              {missing.length === 0 ? (
                <div className="pbp-muted">Nothing — all required details are in.</div>
              ) : (
                missing.map((m) => (
                  <div key={m.key} className="pbp-missing">
                    <div>
                      {m.label}
                      {m.blocking && <span className="pbp-badge pbp-danger pbp-ml">Required</span>}
                      {m.reason && <span className="pbp-rule"> · {m.reason}</span>}
                    </div>
                    <div className="pbp-inline">
                      <input
                        className="pbp-input"
                        placeholder={`Enter ${String(m.label).toLowerCase()}`}
                        value={missingInput[m.key] || ""}
                        onChange={(e) => setMissingInput((p) => ({ ...p, [m.key]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && saveMissing(m)}
                      />
                      <button className="pbp-btn" disabled={busy === `m-${m.key}`} onClick={() => saveMissing(m)}>
                        Save
                      </button>
                    </div>
                  </div>
                ))
              )}
            </section>

            {/* free text */}
            <section>
              <h6 className="pbp-h">Add or correct facts in your own words</h6>
              <textarea
                className="pbp-textarea"
                rows={3}
                placeholder="E.g. the notice should also go to the director, Mr. X…"
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
              />
              <div className="pbp-row">
                <button className="pbp-btn" disabled={!freeText.trim() || busy === "intake"} onClick={submitFreeText}>
                  {busy === "intake" ? "Updating…" : "Update facts"}
                </button>
              </div>
            </section>

            {/* generate */}
            <div className="pbp-generate">
              <select className="pbp-select" value={mode} onChange={(e) => setMode(e.target.value)}>
                {Array.from(new Set([mode, ...modes])).map((m) => (
                  <option key={m} value={m}>{modeLabel(m)}</option>
                ))}
              </select>
              <button className="pbp-primary" disabled={!canGenerate || busy === "generate"} onClick={handleGenerate}>
                {busy === "generate" ? "Drafting…" : draft ? "Generate new draft" : "Generate draft"}
              </button>
              {!canGenerate && (
                <span className="pbp-muted">
                  {blockingCount} required detail(s) and {conflictCount} conflict(s) to resolve first.
                </span>
              )}
            </div>

            {draft && (
              <div className="pbp-row">
                <button className="pbp-btn" onClick={() => setView("draft")}>
                  View latest draft{draft.version ? ` (v${draft.version})` : ""}
                </button>
              </div>
            )}
            </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlaybookPanel;